import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getImportRequestStatus,
  getImportRequestStatusList,
  ImportRequestModel,
  ImportRequestState,
} from "../../clients/BackOfficeApi";
import {
  Button,
  Table,
  TableCaption,
  Tbody,
  Td,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { ImportRequestHeaderRow } from "./ImportRequestHeaderRow";
import { ImportRequestListRow } from "./ImportRequestListRow";
import { useIsAuthenticated } from "@azure/msal-react";
import {
  usePagination,
  withPagination,
} from "../../contexts/PaginationContext";
import { PaginationForm } from "../../elements/PaginationForm";
import { TableLoadingRow } from "../../elements/TableLoadingRow";

interface Props {
  componentRefreshToken?: symbol;
}

export const ImportRequestTable = withPagination<Props>(
  { settingsKey: "importRequests" },
  ({ componentRefreshToken }) => {
    const isAuthenticated = useIsAuthenticated();
    const [importRequests, setImportRequestStatuses] = useState<
      ImportRequestModel[]
    >([]);
    const [tableSpan, setTableSpan] = useState(1);
    const { limit, offset } = usePagination();
    const [loadedOffset, setLoadedOffset] = useState(0);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const fetchImportRequests = useCallback(
      async (signal?: AbortSignal, refetchAll = false) => {
        if (!isAuthenticated) return;

        setLoading(true);

        const query = refetchAll
          ? { Offset: 0, Limit: offset + limit }
          : { Offset: offset, Limit: limit };

        getImportRequestStatusList({
          query,
          signal,
          throwOnError: true,
        })
          .then(({ data }) => {
            if (signal?.aborted) return;

            if (data?.items) {
              const loadedItems = data.items.length;
              setLoadedOffset(query.Offset + loadedItems);
              setImportRequestStatuses((prev) => {
                prev.splice(query.Offset, loadedItems, ...(data.items ?? []));
                return prev.slice(0, query.Offset + loadedItems);
              });
              setTotal(data.totalCount);
            } else {
              setImportRequestStatuses([]);
              setTotal(0);
            }
          })
          .catch((e) => {
            if (signal?.aborted) return;

            throw e;
          })
          .finally(() => setLoading(false));
      },
      [offset, limit, isAuthenticated],
    );

    const moreRowsAvailable = useMemo(
      () => loadedOffset < total,
      [loadedOffset, total],
    );

    useEffect(() => {
      const abortController = new AbortController();

      fetchImportRequests(abortController.signal, true);

      return () => abortController.abort("Component unmounted or re-rendered");
    }, [componentRefreshToken, fetchImportRequests]);

    useEffect(() => {
      const pending = importRequests.filter(
        (ir) => ir.status === ImportRequestState.PENDING,
      );

      if (!pending.length) return;

      const abortController = new AbortController();

      const timeoutId = window.setTimeout(() => {
        Promise.all(
          pending.map((p) =>
            getImportRequestStatus({
              path: { id: p.id },
              signal: abortController.signal,
              throwOnError: true,
            }).then(res => {
              const i = importRequests.findIndex((ir) => ir.id === res.data.id);
              importRequests[i] = res.data;
            }),
          ),
        ).then(() => {
          if (abortController.signal.aborted) return;

          setImportRequestStatuses(importRequests.slice());
        });
      }, 5000);

      abortController.signal.addEventListener("abort", () => {
        window.clearTimeout(timeoutId);
      });

      return () => abortController.abort("Component unmounted or re-rendered");
    }, [importRequests]);

    return (
      <Table variant="striped">
        <TableCaption placement="top" textAlign="left" whiteSpace="nowrap">
          Importeerverzoeken ({total})
          <Button
            color="red.500"
            ml={4}
            onClick={() => fetchImportRequests(undefined, true)}
            variant="link"
          >
            Vernieuwen <RepeatIcon ml={2} />
          </Button>
        </TableCaption>
        <Thead>
          <ImportRequestHeaderRow
            ref={(el: HTMLTableRowElement) =>
              setTableSpan(el?.childElementCount ?? 1)
            }
          />
        </Thead>
        <Tbody>
          <TableLoadingRow colSpan={tableSpan} loading={!moreRowsAvailable && loading} />
          {importRequests.length === 0 && !loading && (
            <Tr>
              <Td colSpan={tableSpan} textAlign="center">
                Geen importeerverzoeken gevonden
              </Td>
            </Tr>
          )}
          {importRequests.map((importRequest) => (
            <ImportRequestListRow
              key={importRequest.id}
              importRequest={importRequest}
              onChange={() => fetchImportRequests(undefined, true)}
            />
          ))}
          {moreRowsAvailable && (
            <Tr>
              <Td colSpan={tableSpan} textAlign="center">
                <PaginationForm offset={loadedOffset} loading={loading} />
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    );
  },
);
